import React, { useEffect, useState } from 'react';
import { X, Clock, ArrowUpRight, MapPin } from 'lucide-react';
import { ordering, contact } from '../mock/mock';

const PLATFORMS = [
  {
    id: 'doordash',
    name: 'DoorDash',
    logo: 'https://cdn.simpleicons.org/doordash/FFFFFF',
    brand: '#FF3008',
    word: '#EB1700',
    hrefKey: 'doordashUrl',
  },
  {
    id: 'ubereats',
    name: 'Uber Eats',
    logo: 'https://cdn.simpleicons.org/ubereats/FFFFFF',
    brand: '#06C167',
    word: '#05913F',
    hrefKey: 'ubereatsUrl',
  },
];

export default function OrderModal({ open, onClose }) {
  const [render, setRender] = useState(open);
  const [show, setShow] = useState(false);
  const [mode, setMode] = useState('pickup');

  useEffect(() => {
    if (open) {
      setRender(true);
      document.body.style.overflow = 'hidden';
      const raf = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(raf);
    }
    setShow(false);
    const t = setTimeout(() => {
      setRender(false);
      setMode('pickup');
      document.body.style.overflow = '';
    }, 380);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!render) return null;

  const isDelivery = mode === 'delivery';

  const hrefFor = (p) => {
    let url = ordering[p.hrefKey];
    if (p.id === 'doordash') {
      url = url.replace('pickup=true', isDelivery ? 'pickup=false' : 'pickup=true');
    } else if (p.id === 'ubereats') {
      url = url.replace('diningMode=DELIVERY', isDelivery ? 'diningMode=DELIVERY' : 'diningMode=PICKUP');
    }
    return url;
  };

  const timeLabel = isDelivery ? 'Delivered in 25-35 min' : 'Ready in 10-15 min';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="order-modal"
      aria-modal="true"
      role="dialog"
    >
      {/* backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/55 backdrop-blur-[3px] transition-opacity duration-300 ${
          show ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* panel */}
      <div
        className={`relative w-full sm:max-w-[440px] bg-white text-[#111111] rounded-t-[28px] sm:rounded-[28px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)] overflow-hidden will-change-transform ${
          show
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 translate-y-6 sm:translate-y-4 scale-[0.98]'
        }`}
        style={{ transition: 'opacity 380ms cubic-bezier(0.16,1,0.3,1), transform 420ms cubic-bezier(0.16,1,0.3,1)' }}
      >
        {/* drag handle (mobile) */}
        <div className="sm:hidden pt-3 pb-1 flex justify-center">
          <span className="h-1.5 w-10 rounded-full bg-black/15" />
        </div>

        <div className="px-7 pt-6 sm:pt-8 pb-8">
          {/* header */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#2C3A28]/60">
                Order online
              </p>
              <h2 className="mt-2 text-[26px] leading-none font-semibold tracking-tight text-[#2C3A28]">
                Al Naturale
              </h2>
              <p className="mt-2 flex items-center gap-1.5 text-[13px] text-[#2C3A28]/70">
                <MapPin size={14} strokeWidth={2} /> {contact.address}
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="order-modal-close"
              className="w-9 h-9 -mr-1 -mt-1 rounded-full flex items-center justify-center text-black/50 hover:text-black hover:bg-black/5 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* toggle */}
          <div className="relative mt-7 grid grid-cols-2 p-1 rounded-full bg-[#E9EBED] border border-[#2C3A28]/15">
            <span
              className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-full bg-[#E9E5D8] shadow-sm"
              style={{
                transition: 'transform 340ms cubic-bezier(0.16,1,0.3,1)',
                transform: isDelivery ? 'translateX(100%)' : 'translateX(0)',
              }}
            />
            <button
              onClick={() => setMode('pickup')}
              data-testid="order-toggle-pickup"
              className={`relative z-10 py-2.5 text-[14px] font-semibold rounded-full transition-colors duration-300 ${
                !isDelivery ? 'text-[#2C3A28]' : 'text-[#2C3A28]/55'
              }`}
            >
              Pick-up
            </button>
            <button
              onClick={() => setMode('delivery')}
              data-testid="order-toggle-delivery"
              className={`relative z-10 py-2.5 text-[14px] font-semibold rounded-full transition-colors duration-300 ${
                isDelivery ? 'text-[#2C3A28]' : 'text-[#2C3A28]/55'
              }`}
            >
              Delivery
            </button>
          </div>

          {/* platforms */}
          <div className="mt-6 space-y-3">
            {PLATFORMS.map((p, i) => (
              <a
                key={p.id}
                href={hrefFor(p)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`order-platform-${p.id}`}
                className="group flex items-center gap-4 rounded-2xl border border-black/10 px-4 py-4 hover:border-black/25 hover:shadow-[0_10px_30px_-16px_rgba(0,0,0,0.6)] transition-all"
                style={{
                  transitionDelay: show ? `${120 + i * 80}ms` : '0ms',
                  transitionProperty: 'opacity, transform, border-color, box-shadow',
                  transitionDuration: '520ms',
                  transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
                  opacity: show ? 1 : 0,
                  transform: show ? 'translateY(0)' : 'translateY(10px)',
                }}
              >
                <span
                  className="flex items-center justify-center w-12 h-12 rounded-xl shrink-0 transition-transform duration-300 group-hover:scale-105"
                  style={{ backgroundColor: p.brand }}
                >
                  <img src={p.logo} alt={`${p.name} logo`} className="w-6 h-6" loading="lazy" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[17px] font-bold tracking-tight" style={{ color: p.word }}>
                    {p.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2 text-[12px] text-black/55">
                    <span className="inline-flex items-center rounded-full bg-black/[0.06] px-2 py-0.5 font-medium">
                      No fee
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock size={12} strokeWidth={2.2} /> {timeLabel}
                    </span>
                  </div>
                </div>
                <ArrowUpRight
                  size={20}
                  className="text-black/30 group-hover:text-black transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 shrink-0"
                />
              </a>
            ))}
          </div>

          <p className="mt-6 text-center text-[11px] text-black/35 tracking-wide">
            You'll be redirected to our partner to complete your order.
          </p>
        </div>
      </div>
    </div>
  );
}
